<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Produktikoner'" :sub-title="icon_name" :title-id="icon_id">
            <template #header-elements>
                <limitless-page-header-button url="/product_icons" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

            <div v-else-if="!busy_loading && icon">
                <div class="row">
                    <div class="col-lg-6">
                        <div class="card">
                            <div class="card-body">
                                <ValidationObserver ref="iconform">
                                    <form @submit.prevent="saveIcon">
                                        <div class="row">
                                            <div class="col-lg-6">
                                                <input-with-validation
                                                    vid="FilePath"
                                                    label="Fil"
                                                    rules="required"
                                                    v-model="icon.FilePath"
                                                />
                                                <input-with-validation
                                                    vid="IconTag"
                                                    label="Tag"
                                                    rules="required"
                                                    v-model="icon.IconTag"
                                                />
                                                <select-with-validation
                                                    vid="Category"
                                                    label="Kategori"
                                                    rules="required"
                                                    v-model="icon.Category"
                                                    :options="[{ id: 'Kök', label: 'Kök' }, { id: 'Miljö', label: 'Miljö' }, { id: 'Tvättråd', label: 'Tvättråd' }]"
                                                    :reduce="(cat) => cat.id"
                                                />
                                            </div>
                                            <div class="col-lg-6">
                                                <div class="form-group">
                                                    <label>Ikon: </label>
                                                    <div>
                                                        <img v-if="icon.Url" :src="icon.Url" :alt="icon.IconText" style="max-width: 43px; max-width: 43px;">
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <label>Text</label>
                                        <div class="row" v-for="lang in languages_tabs" :key="'lang-' + lang.short">
                                            <div class="col-2 col-form-label">{{ lang.name }}</div>
                                            <div class="col-10">
                                                <input-with-validation
                                                    :vid="'translations.' + lang.short + '.IconText'"
                                                    label="Text"
                                                    v-model="icon.translations[lang.short].IconText"
                                                    hide-label
                                                    compact
                                                />
                                            </div>
                                        </div>
                                    </form>
                                </ValidationObserver>
                            </div>
                            <div class="card-footer">
                                <div class="d-flex justify-content-end">
                                    <limitless-button v-if="icon.id > 0" ref="btndeleteicon" type="button" @click="deleteIcon" :busy="busy_saving" icon="icon-cross2" btn-class="btn-danger" class="mr-auto">Radera</limitless-button>
                                    <limitless-button ref="btnsaveicon" type="button" @click="saveIcon" :busy="busy_saving" icon="icon-floppy-disk">Spara</limitless-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div> <!-- /content -->
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import getError from '@services/ErrorHelper';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';
import BackendRepository from '@services/repositories/BackendRepository';

export default {
    name: 'ProductIconEdit',
    components: {
        SelectWithValidation,
        InputWithValidation,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
    },
    props: {
        id: {
            type: [Number, String],
            default: 0,
        },
    },
    data: () => ({
        busy_loading: true,
        busy_saving: false,
        icon: null,
        languages: [],
    }),
    mounted() {
        this.getLanguages();
        this.getIcon();
    },
    methods: {
        getIcon() {
            this.busy_loading = true;
            apiClient.get('/vue/product_icons/' + this.id + '')
                .then((response) => {
                    this.icon = response.data.icon;
                })
                .finally(() => {
                    this.busy_loading = false;
                });
        },
        async saveIcon() {
            const isValid = await this.$refs.iconform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/product_icons/' + this.id, this.icon)
                    .then((response) => {
                        if (parseInt(this.id, 10) !== parseInt(response.data.icon.IconID, 10)) {
                            window.location.href = '/product_icons/' + response.data.icon.IconID;
                        }
                        this.icon = response.data.icon;
                        this.$refs.btnsaveicon.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.iconform.setErrors(parsederror);
                        }
                        this.$refs.btnsaveicon.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        async deleteIcon() {
            let doChange = false;
            await this.$swal({
                text: 'Är du säker att du vill radera denna ikon?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, ta bort!',
                cancelButtonText: 'Avbryt',
            }).then((result) => {
                if (result.isConfirmed) {
                    doChange = true;
                }
            });
            if (doChange) {
                this.busy_saving = true;
                apiClient.delete('/vue/product_icons/' + this.id)
                    .then(() => {
                        window.location.href = '/product_icons';
                        this.busy_saving = false;
                    })
                    .catch(() => {
                        this.$refs.btndeleteicon.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        getLanguages() {
            BackendRepository.getLanguages().then((res) => {
                this.languages = res;
            });
        },
    },
    computed: {
        icon_name() {
            return (this.icon && this.icon.IconID > 0 ? this.icon.IconText : 'Ny ikon');
        },
        icon_id() {
            return (this.icon && this.icon.IconID > 0 ? this.icon.IconID : '');
        },
        languages_tabs() {
            return [...this.languages].sort((a, b) => {
                if (a.short === 'SV') {
                    return -1;
                }
                if (b.short === 'SV') {
                    return 1;
                }
                return 0;
            });
        },
    },
};

</script>
